import React, { useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Input, Label, Spinner, Table } from "reactstrap";

import { Aviso, EstadoTabela, ModalHub, Pill, fieldInputStyle, fmtNumber } from "../_documentosFrete/comum";
import {
  errosDeCampos,
  listarTransportadoras,
  mensagemErroPedido,
  nomeTransportadora,
  notasDosPedidos,
  trocarTransportadora,
} from "../../../services/pedidosApi";

// Trocar a transportadora do pedido (H6, PO1-2380): tudo ou nada nas NFs do
// pedido. Mostra as NFs afetadas e o que bloqueia (CT-e, romaneio, envio ativo)
// antes de chamar; o 409 `troca_bloqueada` do servidor é a trava final. O frete
// simulado é zerado e recalculado pelo fluxo normal (Status_Calculo = 0).

export const MOTIVO = { min: 5, max: 300 };

// Por que a NF bloqueia a troca (espelho da regra do servidor), ou null.
export function bloqueioDaNota(n) {
  if (!n) return null;
  if (n.temCte) return "CT-e vinculado";
  if (n.idRomaneio) return `Em romaneio #${n.idRomaneio}`;
  const envios = Array.isArray(n.envios) ? n.envios : [];
  if (envios.some((e) => ["criando", "pendente", "emitida"].indexOf(e.status) !== -1)) return "Etiqueta/envio ativo";
  return null;
}

export default function TrocarTransportadoraModal({ pedido, onClose }) {
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState("");
  const [notas, setNotas] = useState([]);
  const [transportadoras, setTransportadoras] = useState([]);
  const [idTransportadora, setIdTransportadora] = useState("");
  const [motivo, setMotivo] = useState("");
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [bloqueadas, setBloqueadas] = useState([]); // do 409 do servidor
  const [feito, setFeito] = useState(false);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErroCarga("");
    Promise.all([notasDosPedidos([pedido]), listarTransportadoras()])
      .then(([n, t]) => {
        if (!ativo) return;
        setNotas(n);
        setTransportadoras(t);
      })
      .catch((e) => ativo && setErroCarga(mensagemErroPedido(e, "Não conseguimos carregar as notas do pedido.")))
      .finally(() => ativo && setCarregando(false));
    return () => {
      ativo = false;
    };
  }, [pedido]);

  const atuais = useMemo(() => {
    const vistas = [];
    notas.forEach((n) => {
      if (n.idTransportadora && !vistas.some((t) => String(t.id) === String(n.idTransportadora))) {
        vistas.push({ id: n.idTransportadora, nome: n.transportadora || `#${n.idTransportadora}` });
      }
    });
    return vistas;
  }, [notas]);

  const bloqueiosLocais = notas.map((n) => ({ nota: n, motivo: bloqueioDaNota(n) })).filter((b) => b.motivo);
  const mesmaTransportadora = Boolean(idTransportadora) && atuais.length === 1 && String(atuais[0].id) === String(idTransportadora);
  const motivoOk = motivo.trim().length >= MOTIVO.min && motivo.trim().length <= MOTIVO.max;
  const podeTrocar = !carregando && !salvando && !feito && notas.length > 0 && !bloqueiosLocais.length && Boolean(idTransportadora) && !mesmaTransportadora && motivoOk;

  const trocar = async () => {
    if (!podeTrocar) return;
    setSalvando(true);
    setAviso(null);
    setErros({});
    setBloqueadas([]);
    try {
      const r = await trocarTransportadora({ pedido, idTransportadora, motivo: motivo.trim() });
      const para = (r && r.para && r.para.nome) || nomeTransportadora(transportadoras.find((t) => String(t.id) === String(idTransportadora)));
      setFeito(true);
      setAviso({
        tom: "ok",
        texto: `${fmtNumber((r && r.notas && r.notas.length) || notas.length)} nota(s) transferida(s) para ${para}. O frete será recalculado.`,
      });
    } catch (e) {
      const d = (e && e.response && e.response.data) || {};
      if (d.code === "troca_bloqueada") setBloqueadas(Array.isArray(d.notas) ? d.notas : []);
      setErros(errosDeCampos(e));
      setAviso({ tom: "erro", texto: mensagemErroPedido(e, "Não foi possível trocar a transportadora.") });
    } finally {
      setSalvando(false);
    }
  };

  const fechar = () => {
    if (salvando) return;
    onClose(feito);
  };

  return (
    <ModalHub titulo={`Trocar transportadora · pedido ${pedido}`} icone="tim-icons icon-delivery-fast" onClose={fechar} maxWidth={860}>
      {erroCarga ? <Aviso tom="erro">{erroCarga}</Aviso> : null}
      {carregando ? <EstadoTabela loading textoCarregando="Carregando as notas do pedido..." /> : null}

      {!carregando && !erroCarga ? (
        <div className="docs-frete">
          <p className="docs-frete__secao">Notas afetadas · {fmtNumber(notas.length)}</p>
          {!notas.length ? (
            <Aviso tom="aviso">Nenhuma nota encontrada para este pedido.</Aviso>
          ) : (
            <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive>
              <thead className="text-primary">
                <tr>
                  <th>Nota fiscal</th>
                  <th>Transportadora atual</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {notas.map((n) => {
                  const bloqueio = bloqueioDaNota(n);
                  return (
                    <tr key={n.idNota}>
                      <td>
                        <strong>{n.numero}</strong>
                        <small>Série {n.serie || "-"}</small>
                      </td>
                      <td>{n.transportadora || "-"}</td>
                      <td>{bloqueio ? <Pill tom="warning">{bloqueio}</Pill> : <Pill tom="success">Pode trocar</Pill>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {bloqueiosLocais.length ? (
            <Aviso tom="erro">
              A troca é do pedido inteiro e há nota que não permite: {bloqueiosLocais.map((b) => `NF ${b.nota.numero} (${b.motivo})`).join(", ")}.
            </Aviso>
          ) : null}
          {bloqueadas.length ? (
            <Aviso tom="erro" role="alert">
              O servidor bloqueou a troca: {bloqueadas.map((b) => `NF ${b.numero} (${b.motivo})`).join(", ")}.
            </Aviso>
          ) : null}

          {notas.length && !bloqueiosLocais.length && !feito ? (
            <div className="docs-frete__form" style={{ marginTop: "1rem" }}>
              <FormGroup>
                <Label for="pedido-troca-transportadora">Nova transportadora *</Label>
                <Input
                  id="pedido-troca-transportadora"
                  type="select"
                  value={idTransportadora}
                  onChange={(e) => setIdTransportadora(e.target.value)}
                  disabled={salvando}
                  invalid={Boolean(erros.idTransportadora)}
                  style={fieldInputStyle}
                >
                  <option value="">Selecione...</option>
                  {transportadoras.map((t) => (
                    <option key={t.id} value={t.id}>
                      {nomeTransportadora(t)}
                    </option>
                  ))}
                </Input>
                {erros.idTransportadora ? <span className="docs-frete__erro-campo">{erros.idTransportadora}</span> : null}
                {mesmaTransportadora ? <span className="docs-frete__erro-campo">As notas do pedido já estão nesta transportadora.</span> : null}
                {atuais.length ? <span className="docs-frete__dica">Atual: {atuais.map((t) => t.nome).join(", ")}.</span> : null}
              </FormGroup>
              <FormGroup>
                <Label for="pedido-troca-motivo">
                  Motivo * ({MOTIVO.min} a {MOTIVO.max} caracteres)
                </Label>
                <Input
                  id="pedido-troca-motivo"
                  type="textarea"
                  rows={3}
                  maxLength={MOTIVO.max}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  disabled={salvando}
                  invalid={Boolean(erros.motivo)}
                  style={fieldInputStyle}
                />
                {erros.motivo ? <span className="docs-frete__erro-campo">{erros.motivo}</span> : null}
              </FormGroup>
              <Aviso tom="aviso">
                O frete simulado das notas será zerado e <strong>recalculado</strong> para a nova transportadora. Até o recálculo, o pedido fica "sem previsão".
              </Aviso>
            </div>
          ) : null}
        </div>
      ) : null}

      {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}

      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={fechar} disabled={salvando}>
          Fechar
        </Button>
        {!feito ? (
          <Button color="primary" size="sm" onClick={trocar} disabled={!podeTrocar}>
            {salvando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
            {salvando ? "Trocando..." : "Trocar transportadora"}
          </Button>
        ) : null}
      </div>
    </ModalHub>
  );
}
